/**
 * TISA COINS SHOP SYSTEM API
 *
 * Functions to interact with shop purchases, inventory, and usage.
 * Calls PostgreSQL functions created in migration 20250109000001.
 */

import { supabase } from "./supabase";
import type {
  ShopPurchase,
  InventoryItem,
  ShopUsageLog,
  PurchaseItemForm,
  FulfillPurchaseForm,
  RefundPurchaseForm,
  UseItemForm,
  AwardItemForm,
  PurchaseResponse,
  UseItemResponse,
  APIResponse,
  FulfillmentQueueItem,
  PendingPurchase,
  InventorySummary,
} from "../types/tisa-coins";

// ============================================================================
// STUDENT FUNCTIONS - Purchase & Use Items
// ============================================================================

/**
 * Purchase an item from the shop
 * Calls PostgreSQL function: purchase_shop_item()
 */
export const purchaseItem = async (
  userId: string,
  form: PurchaseItemForm,
): Promise<APIResponse<PurchaseResponse>> => {
  try {
    const { data, error } = await supabase.rpc("purchase_shop_item", {
      p_user_id: userId,
      p_item_id: form.item_id,
      p_item_name: form.item_name,
      p_price: form.price,
      p_category: form.category,
      p_rarity: form.rarity,
      p_notes: form.notes || {},
    });

    if (error) {
      // Handle specific error cases
      if (error.message.includes("INSUFFICIENT_FUNDS")) {
        return {
          success: false,
          error: "Insufficient vault balance",
          message: "You need more coins to purchase this item",
        };
      }
      if (error.message.includes("USER_NOT_FOUND")) {
        return {
          success: false,
          error: "User not found",
          message: "Student account not found",
        };
      }
      throw error;
    }

    // Fetch updated vault balance (students are in children table)
    const { data: userData, error: userError } = await supabase
      .from("children")
      .select("coin_events")
      .eq("id", userId)
      .single();

    if (userError) throw userError;

    // Calculate new vault balance
    const coinEvents = Array.isArray(userData.coin_events)
      ? userData.coin_events
      : [];
    const vaultBalance = coinEvents
      .filter((e: any) => e.account_type === "vault" && e.status === "approved")
      .reduce((sum: number, e: any) => sum + e.amount, 0);

    return {
      success: true,
      data: {
        purchase: data as ShopPurchase,
        new_vault_balance: vaultBalance,
        message: `${form.item_name} purchased! Go to your teacher's desk to collect it.`,
      },
    };
  } catch (error: any) {
    console.error("Shop purchase error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to purchase item",
    };
  }
};

/**
 * Get student's pending purchases (waiting for delivery)
 */
export const getPendingPurchases = async (
  userId: string,
): Promise<APIResponse<PendingPurchase[]>> => {
  try {
    const { data, error } = await supabase
      .from("shop_purchases")
      .select("*")
      .eq("student_id", userId)
      .eq("status", "pending_delivery")
      .order("purchased_at", { ascending: true });

    if (error) throw error;

    const pending: PendingPurchase[] = (data as ShopPurchase[]).map(
      (purchase) => ({
        purchase,
        days_waiting: Math.floor(
          (Date.now() - new Date(purchase.purchased_at).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      }),
    );

    return {
      success: true,
      data: pending,
    };
  } catch (error: any) {
    console.error("Get pending purchases error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get student's inventory
 */
export const getInventory = async (
  userId: string,
): Promise<APIResponse<InventoryItem[]>> => {
  try {
    const { data, error } = await supabase
      .from("student_inventory")
      .select("*")
      .eq("student_id", userId)
      .order("acquired_at", { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data as InventoryItem[],
    };
  } catch (error: any) {
    console.error("Get inventory error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get student's inventory summary
 */
export const getInventorySummary = async (
  userId: string,
): Promise<APIResponse<InventorySummary>> => {
  try {
    const inventoryResult = await getInventory(userId);
    if (!inventoryResult.success || !inventoryResult.data) {
      throw new Error("Failed to fetch inventory");
    }

    const items = inventoryResult.data;

    const summary: InventorySummary = {
      total_items: items.length,
      by_category: {
        supplies: items.filter((i) => i.category === "supplies").length,
        privileges: items.filter((i) => i.category === "privileges").length,
        events: items.filter((i) => i.category === "events").length,
        customization: items.filter((i) => i.category === "customization")
          .length,
        consumables: items.filter((i) => i.category === "consumables").length,
      },
      by_rarity: {
        common: items.filter((i) => i.rarity === "common").length,
        rare: items.filter((i) => i.rarity === "rare").length,
        epic: items.filter((i) => i.rarity === "epic").length,
        legendary: items.filter((i) => i.rarity === "legendary").length,
      },
      consumables_ready: items.filter(
        (i) => i.status === "active" && i.use_effect !== null,
      ),
      consumables_used: items.filter((i) => i.status === "consumed"),
    };

    return {
      success: true,
      data: summary,
    };
  } catch (error: any) {
    console.error("Get inventory summary error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Use a consumable item from inventory
 * Calls PostgreSQL function: use_inventory_item()
 */
export const useInventoryItem = async (
  userId: string,
  form: UseItemForm,
): Promise<APIResponse<UseItemResponse>> => {
  try {
    const { data, error } = await supabase.rpc("use_inventory_item", {
      p_student_id: userId,
      p_inventory_id: form.inventory_id,
      p_effect_data: form.effect_data || {},
    });

    if (error) {
      if (error.message.includes("ITEM_ALREADY_CONSUMED")) {
        return {
          success: false,
          error: "Item already consumed",
          message: "This item has no uses remaining",
        };
      }
      if (error.message.includes("ITEM_NO_USES_REMAINING")) {
        return {
          success: false,
          error: "No uses remaining",
          message: "This item has been fully consumed",
        };
      }
      throw error;
    }

    const usageLog = data as ShopUsageLog;

    // Get updated item status
    const { data: itemData, error: itemError } = await supabase
      .from("student_inventory")
      .select("status, used_count, times_usable")
      .eq("id", form.inventory_id)
      .single();

    if (itemError) throw itemError;

    return {
      success: true,
      data: {
        usage_log: usageLog,
        item_status: itemData.status as "active" | "consumed",
        uses_remaining:
          (itemData.times_usable ?? 0) - (itemData.used_count ?? 0),
        message: `Item used successfully! ${(itemData.times_usable ?? 0) - (itemData.used_count ?? 0)} uses remaining.`,
      },
    };
  } catch (error: any) {
    console.error("Use inventory item error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to use item",
    };
  }
};

/**
 * Get student's usage history
 */
export const getUsageHistory = async (
  userId: string,
): Promise<APIResponse<ShopUsageLog[]>> => {
  try {
    const { data, error } = await supabase
      .from("shop_usage_log")
      .select("*")
      .eq("student_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) throw error;

    return {
      success: true,
      data: data as ShopUsageLog[],
    };
  } catch (error: any) {
    console.error("Get usage history error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// ============================================================================
// TEACHER FUNCTIONS - Fulfillment, Refunds, Awards
// ============================================================================

/**
 * Get all pending shop purchases (school-wide fulfillment queue)
 */
export const getFulfillmentQueue = async (): Promise<
  APIResponse<FulfillmentQueueItem[]>
> => {
  try {
    const { data, error } = await supabase
      .from("shop_purchases")
      .select("*")
      .eq("status", "pending_delivery")
      .order("purchased_at", { ascending: true });

    if (error) throw error;

    const queue: FulfillmentQueueItem[] = (data as ShopPurchase[]).map(
      (purchase) => {
        const daysWaiting = Math.floor(
          (Date.now() - new Date(purchase.purchased_at).getTime()) /
            (1000 * 60 * 60 * 24),
        );

        return {
          purchase,
          student_name: purchase.student_name,
          student_class: purchase.student_class,
          days_waiting: daysWaiting,
          priority:
            daysWaiting >= 3 ? "high" : daysWaiting >= 1 ? "normal" : "low",
        };
      },
    );

    return {
      success: true,
      data: queue,
    };
  } catch (error: any) {
    console.error("Get fulfillment queue error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Fulfill a shop purchase (mark item as delivered)
 * Calls PostgreSQL function: fulfill_shop_purchase()
 */
export const fulfillPurchase = async (
  teacherId: string,
  form: FulfillPurchaseForm,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase.rpc("fulfill_shop_purchase", {
      p_teacher_id: teacherId,
      p_purchase_id: form.purchase_id,
      p_use_effect: form.use_effect ?? undefined,
      p_times_usable: form.times_usable || 1,
    });

    if (error) {
      if (error.message.includes("PURCHASE_NOT_FOUND")) {
        return {
          success: false,
          error: "Purchase not found",
          message: "Could not find this purchase",
        };
      }
      if (error.message.includes("PURCHASE_ALREADY_FULFILLED")) {
        return {
          success: false,
          error: "Already fulfilled",
          message: "This purchase has already been delivered or refunded",
        };
      }
      throw error;
    }

    return {
      success: true,
      message: "Item delivered successfully and added to student inventory",
    };
  } catch (error: any) {
    console.error("Fulfill purchase error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to fulfill purchase",
    };
  }
};

/**
 * Refund a shop purchase (return coins to student)
 * Calls PostgreSQL function: refund_shop_purchase()
 */
export const refundPurchase = async (
  teacherId: string,
  form: RefundPurchaseForm,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase.rpc("refund_shop_purchase", {
      p_teacher_id: teacherId,
      p_purchase_id: form.purchase_id,
      p_reason: form.reason,
    });

    if (error) {
      if (error.message.includes("CAN_ONLY_REFUND_PENDING_PURCHASES")) {
        return {
          success: false,
          error: "Cannot refund",
          message: "Can only refund purchases that are pending delivery",
        };
      }
      throw error;
    }

    return {
      success: true,
      message:
        "Purchase refunded successfully. Coins returned to student vault.",
    };
  } catch (error: any) {
    console.error("Refund purchase error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to refund purchase",
    };
  }
};

/**
 * Award an item directly to a student (no coins required)
 * Calls PostgreSQL function: award_item_to_student()
 */
export const awardItemToStudent = async (
  teacherId: string,
  form: AwardItemForm,
): Promise<APIResponse<InventoryItem>> => {
  try {
    const { data, error } = await supabase.rpc("award_item_to_student", {
      p_teacher_id: teacherId,
      p_student_id: form.student_id,
      p_item_id: form.item_id,
      p_item_name: form.item_name,
      p_category: form.category,
      p_rarity: form.rarity || "common",
      p_use_effect: form.use_effect ?? undefined,
      p_times_usable: form.times_usable || 1,
    });

    if (error) {
      if (error.message.includes("PERMISSION_DENIED")) {
        return {
          success: false,
          error: "Permission denied",
          message: "Only teachers can award items",
        };
      }
      throw error;
    }

    return {
      success: true,
      data: data as InventoryItem,
      message: `${form.item_name} awarded successfully to student`,
    };
  } catch (error: any) {
    console.error("Award item error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to award item",
    };
  }
};

/**
 * Get purchase history for a specific student (teacher view)
 */
export const getStudentPurchaseHistory = async (
  studentId: string,
): Promise<APIResponse<ShopPurchase[]>> => {
  try {
    const { data, error } = await supabase
      .from("shop_purchases")
      .select("*")
      .eq("student_id", studentId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data as ShopPurchase[],
    };
  } catch (error: any) {
    console.error("Get student purchase history error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// ============================================================================
// ANALYTICS FUNCTIONS
// ============================================================================

/**
 * Get shop analytics (total purchases, popular items, etc.)
 */
export const getShopAnalytics = async (): Promise<
  APIResponse<{
    total_purchases: number;
    total_fulfilled: number;
    total_refunded: number;
    total_revenue: number;
    popular_items: { item_id: string; item_name: string; count: number }[];
  }>
> => {
  try {
    const { data, error } = await supabase.from("shop_purchases").select("*");

    if (error) throw error;

    const purchases = data as ShopPurchase[];

    // Calculate stats
    const total_purchases = purchases.length;
    const total_fulfilled = purchases.filter(
      (p) => p.status === "fulfilled",
    ).length;
    const total_refunded = purchases.filter(
      (p) => p.status === "refunded",
    ).length;
    const total_revenue = purchases
      .filter((p) => p.status === "fulfilled")
      .reduce((sum, p) => sum + p.price_paid, 0);

    // Popular items
    const itemCounts = purchases.reduce(
      (acc, p) => {
        if (p.status === "fulfilled") {
          if (!acc[p.item_id]) {
            acc[p.item_id] = {
              item_id: p.item_id,
              item_name: p.item_name,
              count: 0,
            };
          }
          acc[p.item_id].count++;
        }
        return acc;
      },
      {} as Record<
        string,
        { item_id: string; item_name: string; count: number }
      >,
    );

    const popular_items = Object.values(itemCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      success: true,
      data: {
        total_purchases,
        total_fulfilled,
        total_refunded,
        total_revenue,
        popular_items,
      },
    };
  } catch (error: any) {
    console.error("Get shop analytics error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};
